import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ButtonContainer,
  ContentSection,
  Fieldset,
  Form,
  FormSection,
  FormSectionGroup,
  Layout,
  PageWrap,
  SectionHeading,
  Spinner,
} from '@vwfs-bronson/bronson-react';
import { Formik, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { SpinnerSection } from '../../../components/SpinnerSection';
import { CarOptionsPrFamilies } from '../../../resources/base/models/carOptions';
import { QuoteFlow } from '../../../resources/base/models/enums';
import {
  FuelType,
  fuelTypesList,
  VehicleType,
  VehicleTypesEnumManufacturer,
} from '../../../resources/base/models/vehicleTypes';
import { Analytics } from '../../../services/analytics';
import { configVehiclePageEvent } from '../../../services/analytics/analyticsEvents';
import { getCatalogVehicle } from '../../../services/api/miles/catalogVehicleDetailsPrice';
import { mapCreateQuoteRequest } from '../../../services/api/miles/mapping';
import {
  calculateQuote,
  createQuote,
  getBTOVehiclesData,
  getQuoteVehicleOptions,
  getUsedStockVehiclesData,
  getVehicleGroupModels,
  getVehicleManufacturers,
} from '../../../services/api/miles/miles-services';
import {
  assertDefined,
  getDisplayedManufacturerLabel,
  getMilesCatalogVehicleIdFromVehicle,
  isEngineElectric,
  isEngineElectricOrHybrid,
  waitForMiles,
} from '../../../services/common/miscUtils';
import { resetQuoteSpecificReduxData, store } from '../../../services/redux';
import {
  btoConfigSelectors,
  setBtoConfigLocked,
  setConfigurationString,
} from '../../../services/redux/features/btoConfig.slice';
import { customSelectors } from '../../../services/redux/features/custom.slice';
import { customerSelectors } from '../../../services/redux/features/customer.slice';
import { loadingActions } from '../../../services/redux/features/loading.slice';
import {
  quoteSelectors,
  setAvailableRims,
  setIsNewCopiedQuoteSubmitted,
  setQuoteData,
} from '../../../services/redux/features/quote.slice';
import { useNavigate } from '../../../services/routing/navigate';
import { ConfigurationsSection, VehicleConfigData } from '../../../types/vehicleConfig';
import { DemoCarErrorModal } from '../../DemoCarErrorModal/DemoCarErrorModal';
import { NewCopiedQuoteModal } from '../../NewCopiedQuoteModal.tsx/NewCopiedQuoteModal';
import { calculateQuoteFinancedAmount } from '../Step3/calculations';
import { TotalPriceField } from './fieldset';
import { AdditionalConfigurations } from './FinancialForms/AdditionalConfigurations';
import { NewVehicleBaseForm } from './FinancialForms/BaseForms/NewVehicleBaseForm';
import { StockVehicleBaseForm } from './FinancialForms/BaseForms/StockVehicleBaseForm';
import { UsedVehicleBaseForm } from './FinancialForms/BaseForms/UsedVehicleBaseForm';
import { getPrefillOrDefaultValues } from './initial-values-financial';
import { buildPayloadForNewCopiedQuote } from './newCopiedQuotePayloadData';
import { NoVehiclesErrorModal } from './NoVehiclesErrorModal';
import { VehicleConfigurationFinancialSchema } from './validationSchema';

export const ConfigVehicleFinancial: FunctionComponent = () => {
  const { values: stepValues } = useFormikContext<VehicleConfigData>();
  const { t } = useTranslation();
  const [showDemoCarModal, setShowDemoCarModal] = useState<boolean>(false);
  const [showNewCopiedQuoteModal, setShowNewCopiedQuoteModal] = useState<boolean>(false);
  const [demoCarCheckbox, setDemoCarCheckbox] = useState(false);
  const [originalCatalogPrice, setOriginalCatalogPrice] = useState(0);
  const jatoId = useSelector(btoConfigSelectors.selectJatoId);
  const isQuoteEditable = useSelector(quoteSelectors.selectIsQuoteEditable);

  const quote = useSelector(quoteSelectors.selectQuote);

  const [isLoading, setIsLoading] = useState(false);
  const [baseConfigurationSubmitted, setBaseConfigurationSubmittedCorrectly] = useState(false);
  const [additionalConfigurationSubmitted, setAdditionalConfigurationSubmitted] = useState(false);

  const [expandedSection, setExpandedSection] = useState<ConfigurationsSection | null>(
    ConfigurationsSection.BASECONFIGURATION
  );

  const [manufacturersList, setManufacturersList] = useState<
    Array<{
      value: VehicleIntegration.Components.Schemas.ManufacturerType;
      label: VehicleIntegration.Components.Schemas.ManufacturerType | 'Volkswagen Utilitaires';
    }>
  >([]);
  const [vehicleGroupModelsList, setVehicleGroupModelsList] = useState<
    Array<{ value: string; label: string; id: string }>
  >([]);
  const [vehicleModelsList, setVehicleModelsList] = useState<Array<{ value: string; label: string }>>([]);

  const [allDataForAllVehicles, setAllDataForAllVehicles] = useState<
    Array<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>
  >([]);

  const state = store.getState();
  const [milesVehicleIdNew, setMilesVehicleIdNew] =
    useState<VehicleIntegration.Components.Schemas.CatalogVehicleDataResponse>();
  const [co2EmissionsIsSetByAPI, setCo2EmissionsIsSetByAPI] = useState(false);
  const [fiscalPower, setFiscalPower] = useState<number | undefined>(undefined);
  const [showWarningPopup, setShowWarningPopup] = useState<boolean>(false);
  const quoteFlow = quoteSelectors.selectQuoteFlow(state);
  const navigate = useNavigate();
  const quoteId = quoteSelectors.selectQuoteId(state);
  const quoteData = quoteSelectors.selectQuote(state);
  const rimOptions = quoteSelectors.selectAvailableRims(state);

  const additionalConfigurationsFromState: any = useRef({});
  const [scheduleSubmit, setScheduleSubmit] = useState<boolean>(false);
  const vehicleType = useSelector(customSelectors.selectVehicleType);
  const isCopyModifyFlow = quoteFlow === QuoteFlow.COPY || quoteFlow === QuoteFlow.MODIFY;
  const [formInitialValues, setFormInitialValues] = useState<VehicleConfigData>();
  const newCopiedQuoteSubmitted = useSelector(quoteSelectors.selectIsNewCopiedQuoteSubmitted);
  const [skipQuoteModalCheck, setSkipQuoteModalCheck] = useState(false);
  const previousValuesRef = useRef<VehicleConfigData>({});
  const isFormReadOnly = !isQuoteEditable;
  const hasActiveMdtConfig = useSelector(btoConfigSelectors.selectBtoConfigLocked);
  const isNewVehicleMdtActive = vehicleType === VehicleType.NEW && hasActiveMdtConfig;

  const modifyLabel = t('step1:configVehicle:modify');
  const continueLabel = t('step1:configVehicle:continueButton');

  // Show "Modifier" label if quote exists and MDT config is active; otherwise "Suivant".
  const continueButtonLabel = quoteId && !isNewVehicleMdtActive ? modifyLabel : continueLabel;

  const quoteDataAvailable = useMemo(() => {
    return quoteData && Object.keys(quoteData).length > 0;
  }, [quoteData]);

  useEffect(() => {
    if (!vehicleType) {
      return;
    }

    const prefillData: VehicleConfigData = quoteDataAvailable
      ? getPrefillOrDefaultValues(vehicleType, quoteData, rimOptions)
      : getPrefillOrDefaultValues(vehicleType);

    if (vehicleType === VehicleType.NEW && continueButtonLabel === continueLabel) {
      setExpandedSection(ConfigurationsSection.BASECONFIGURATION);
      setBaseConfigurationSubmittedCorrectly(false);
    } else if (quoteDataAvailable) {
      setBaseConfigurationSubmittedCorrectly(true);
      setExpandedSection(ConfigurationsSection.ADDITIONALCONFIGURATION);
    }
    if (!isQuoteEditable) {
      setAdditionalConfigurationSubmitted(true);
      setExpandedSection(null);
    }
    setFormInitialValues(prefillData);
    previousValuesRef.current = prefillData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Analytics.push(configVehiclePageEvent);
  }, []);

  const getVehicleTypeCarForManufacturerCatalog = (type) => {
    switch (type) {
      case VehicleType.NEW:
        return VehicleTypesEnumManufacturer.BTO;
      case VehicleType.STOCK:
        return VehicleTypesEnumManufacturer.STOCK;
      case VehicleType.USED:
        return VehicleTypesEnumManufacturer.USED;
      default:
        return '';
    }
  };

  const options = useSelector(btoConfigSelectors.selectBtoOptions);
  const btoPrices = useSelector(btoConfigSelectors.selectBtoPrices);
  const milesCustomerId = useSelector(customerSelectors.selectCustomerId);
  const previousConfigurationString = useSelector(btoConfigSelectors.selectPreviousConfigurationString);
  const configurationString = useSelector(btoConfigSelectors.selectConfigurationString);

  const dispatch = useDispatch();

  const getRims = async (quoteId: string) => {
    store.dispatch(loadingActions.setLoading(true));
    try {
      if (vehicleType !== VehicleType.USED) {
        return;
      }

      const rimList =
        (await getQuoteVehicleOptions(quoteId)).vehicleData?.model?.options?.filter(
          (option) => option.prFamily === CarOptionsPrFamilies.Rim
        ) ?? [];

      store.dispatch(setAvailableRims(rimList));
    } catch (error) {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  useEffect(() => {
    const getRimsData = async () => {
      if (quoteDataAvailable && quoteId) {
        await getRims(quoteId);
      }
    };

    getRimsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteDataAvailable, quoteId]);

  const createQuoteAndUpdateData = async (values): Promise<string | null> => {
    const selectedModel = vehicleModelsList.find((model) => model?.value === values?.vehicleMilesId);
    if (selectedModel || milesVehicleIdNew) {
      const selectedVehicle = allDataForAllVehicles?.filter(
        (vehicle) => selectedModel?.value === getMilesCatalogVehicleIdFromVehicle(vehicle)
      );

      if (!vehicleType || (vehicleType !== VehicleType.NEW && selectedVehicle && selectedVehicle.length === 0)) {
        return null;
      }

      dispatch(loadingActions.setLoading(true));

      const createQuoteRequest = mapCreateQuoteRequest(
        {
          ...values,
          options,
          netPriceWithoutOptions: btoPrices.netPriceWithoutOptions,
          milesCustomerId,
          quoteId,
        },
        vehicleType === VehicleType.NEW ? milesVehicleIdNew : selectedVehicle[0]
      );

      const quote = await createQuote(createQuoteRequest);

      const newQuoteId = assertDefined(quote.financialProduct?.offers?.[0].offerId);

      await waitForMiles();

      store.dispatch(setQuoteData(quote));
      const vehicleDataToUpdate: Partial<QuoteSchema.Schemas.VehicleDataCalculateSalesQuoteRequest> = {};

      if (values.fiscalPower && vehicleType !== VehicleType.USED) {
        vehicleDataToUpdate.fiscalPower = values.fiscalPower;
      }
      if (values.dinHp && vehicleType !== VehicleType.USED) {
        vehicleDataToUpdate.model = {
          ...(vehicleDataToUpdate.model ?? {}),
          engine: {
            powerDetails: { powerValues: [{ unit: 'HORSEPOWER', value: values.dinHp }] },
          },
        };
      }
      const updatePayload = newCopiedQuoteSubmitted ? buildPayloadForNewCopiedQuote(quoteData, vehicleType) : {};
      const mergedModel = {
        ...(updatePayload.vehicleData?.model ?? {}),
        ...(vehicleDataToUpdate.model ?? {}),
      };

      const mergedVehicleData = {
        ...(updatePayload.vehicleData ?? {}),
        ...vehicleDataToUpdate,
        ...(Object.keys(mergedModel).length > 0 && { model: mergedModel }),
      };

      const finalPayload: QuoteSchema.Schemas.CalculateSalesQuoteRequest = {
        ...updatePayload,
        ...(Object.keys(mergedVehicleData).length > 0 && { vehicleData: mergedVehicleData }),
      };

      if (Object.keys(finalPayload).length > 0) {
        const newCalculatedQuote = await calculateQuote(newQuoteId!, finalPayload);
        store.dispatch(setQuoteData(newCalculatedQuote));
      }

      dispatch(loadingActions.setLoading(false));

      return newQuoteId;
    }

    return null;
  };

  const getVehicleManufacturersData = async () => {
    store.dispatch(loadingActions.setLoading(true));
    try {
      if (vehicleType === VehicleType.USED || vehicleType === VehicleType.STOCK) {
        const manufacturers = await getVehicleManufacturers(vehicleType);

        const newManufacturersList = manufacturers?.map((manufacturer) => {
          const label = getDisplayedManufacturerLabel(manufacturer);

          return { value: manufacturer, label };
        });
        setManufacturersList(newManufacturersList);
      }
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  const getModelYearLimits = (vehicleType: VehicleType) => {
    const currentYear = new Date().getFullYear();
    return {
      modelYearLowerLimit: `${vehicleType === VehicleType.USED ? currentYear - 8 : currentYear - 2}-01-01`,
      // Increase by 2 years because dealer want to sell models for the next two years
      modelYearUpperLimit: `${currentYear + 2}-12-31`,
    };
  };

  const fetchVehicleGroupData = async (values, carTypeFormatted) => {
    store.dispatch(loadingActions.setLoading(true));
    try {
      if (vehicleType === VehicleType.USED || vehicleType === VehicleType.STOCK) {
        if (values?.brand && carTypeFormatted) {
          const { modelYearLowerLimit, modelYearUpperLimit } = getModelYearLimits(vehicleType);
          const vehicleGroupModelsData = await getVehicleGroupModels(
            values?.brand,
            carTypeFormatted,
            modelYearLowerLimit,
            modelYearUpperLimit
          );
          const newVehicleGroupModelsList = vehicleGroupModelsData?.vehicles.map((item) => ({
            value: item?.model?.name,
            label: item?.model?.name,
            id: item?.externalVehicleIdentifiers[0]?.id,
          }));
          setVehicleGroupModelsList(newVehicleGroupModelsList);
        }
      }
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  const fetchCatalogVehicleData = async (values, carTypeFormatted) => {
    store.dispatch(loadingActions.setLoading(true));
    try {
      if (vehicleType === VehicleType.USED || vehicleType === VehicleType.STOCK) {
        if (values?.modelGroup) {
          const selectedGroupModel = vehicleGroupModelsList.find(
            (modelGroup) => modelGroup?.value === values?.modelGroup
          );
          if (selectedGroupModel && carTypeFormatted) {
            const { modelYearLowerLimit, modelYearUpperLimit } = getModelYearLimits(vehicleType);
            const vehicleModelsData = await getUsedStockVehiclesData(
              selectedGroupModel?.id,
              carTypeFormatted,
              modelYearLowerLimit,
              modelYearUpperLimit
            );
            setAllDataForAllVehicles(vehicleModelsData.vehicles);
            const vehicleModelsList = vehicleModelsData?.vehicles.map((item) => ({
              value: getMilesCatalogVehicleIdFromVehicle(item) || '',
              label: item?.model?.description || '',
            }));
            setVehicleModelsList(vehicleModelsList);
          }
        }
      }
    } catch {
      navigate.error();
    } finally {
      store.dispatch(loadingActions.setLoading(false));
    }
  };

  useEffect(() => {
    if (expandedSection === ConfigurationsSection.ADDITIONALCONFIGURATION) {
      setAdditionalConfigurationSubmitted(false);
    }
  }, [expandedSection]);

  return formInitialValues === undefined ? (
    <SpinnerSection />
  ) : (
    <>
      <PageWrap>
        <ContentSection className="u-pv-none">
          <SectionHeading level={2}>{t('step1:configVehicle:title')}</SectionHeading>
          <FormSectionGroup>
            {/* Base Configuration */}
            <ContentSection.ComponentWrapper>
              <FormSection
                key="baseConfiguration"
                id="baseConfiguration"
                title={t('step1:configVehicle:baseConfiguration:title')}
                status={baseConfigurationSubmitted ? 'success' : undefined}
                defaultOpen={expandedSection === ConfigurationsSection.BASECONFIGURATION}
                onChange={(open: boolean) => {
                  setExpandedSection(open ? ConfigurationsSection.BASECONFIGURATION : null);
                }}
              >
                <Formik
                  initialValues={formInitialValues}
                  enableReinitialize
                  validationSchema={
                    isQuoteEditable
                      ? VehicleConfigurationFinancialSchema(demoCarCheckbox, expandedSection, vehicleType)
                      : undefined
                  }
                  onSubmit={async (values) => {
                    // VehicleType.New: Get back to car configurator
                    if (vehicleType === VehicleType.NEW && continueButtonLabel === modifyLabel) {
                      navigate.configurator();
                      return;
                    }

                    // Check if current form values are the same as previous submission
                    if (isEqual(values, previousValuesRef.current)) {
                      // Specifically for NEW: check if configurationString has changed (user modified MDT config) if not, create new quote
                      if (
                        !(vehicleType === VehicleType.NEW && !isEqual(previousConfigurationString, configurationString))
                      ) {
                        setBaseConfigurationSubmittedCorrectly(true);
                        setExpandedSection(ConfigurationsSection.ADDITIONALCONFIGURATION);
                        dispatch(setBtoConfigLocked(false));
                        return;
                      }
                    }

                    // Show modal if a quote exists, and skipCheck is false.
                    // skipCheck prevents the modal from reopening immediately after user confirms it.
                    if (!skipQuoteModalCheck && !!quoteId) {
                      setShowNewCopiedQuoteModal(true);
                      return;
                    }
                    setSkipQuoteModalCheck(false);
                    previousValuesRef.current = values;
                    const valuesWithOriginalCatalogPrice = { ...values, catalogPrice: originalCatalogPrice };
                    setIsLoading(true);
                    try {
                      await createQuoteAndUpdateData(valuesWithOriginalCatalogPrice);

                      setBaseConfigurationSubmittedCorrectly(true);
                      setExpandedSection(ConfigurationsSection.ADDITIONALCONFIGURATION);
                      dispatch(setBtoConfigLocked(false));
                      setIsLoading(false);
                    } catch {
                      setBaseConfigurationSubmittedCorrectly(false);
                      setIsLoading(false);
                      navigate.error();
                    }
                  }}
                >
                  {(formProps) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const [configIsPrefilledWithNewData, setConfigIsPrefilledWithNewData] = useState(false);
                    const { values, handleSubmit, setFieldValue, setFieldTouched } = formProps;
                    const carTypeFormatted = getVehicleTypeCarForManufacturerCatalog(vehicleType);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      setDemoCarCheckbox(Boolean(values.isDemo));
                    }, [values.isDemo]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      if (!scheduleSubmit) return;

                      const shouldSubmitForm =
                        skipQuoteModalCheck && newCopiedQuoteSubmitted && !showNewCopiedQuoteModal;

                      if (shouldSubmitForm) {
                        formProps.submitForm();
                        setScheduleSubmit(false);
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [scheduleSubmit, skipQuoteModalCheck, newCopiedQuoteSubmitted, showNewCopiedQuoteModal]);

                    additionalConfigurationsFromState.current = {
                      values,
                      isValid: formProps.isValid,
                      dirty: formProps.dirty,
                    };
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      getVehicleManufacturersData();
                    }, []);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      if (!vehicleType || vehicleType === VehicleType.NEW) return;
                      fetchVehicleGroupData(values, carTypeFormatted);
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [values?.brand]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      if (!vehicleType || vehicleType === VehicleType.NEW) return;
                      fetchCatalogVehicleData(values, carTypeFormatted);
                      if (isCopyModifyFlow) {
                        setFieldValue('modelDescription', formInitialValues.modelDescription || '');
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [values?.modelGroup, vehicleGroupModelsList, formInitialValues?.modelDescription]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      async function fetchBTOVehiclesData() {
                        if (vehicleType === VehicleType.NEW && jatoId) {
                          store.dispatch(loadingActions.setLoading(true));
                          const newVehiclesData = await getBTOVehiclesData(jatoId, carTypeFormatted);

                          if (
                            !newVehiclesData.vehicles ||
                            (newVehiclesData.vehicles && newVehiclesData.vehicles.length === 0)
                          ) {
                            const prefillValues = getPrefillOrDefaultValues(vehicleType);
                            setFormInitialValues(prefillValues);
                            formProps.resetForm({ values: prefillValues });
                            setShowWarningPopup(true);
                            navigate.error();
                            throw new Error('Empty vehicle data.');
                          }

                          setMilesVehicleIdNew(newVehiclesData?.vehicles[0]);
                          const modelGroup = newVehiclesData?.vehicles.map((item) => ({
                            value: item?.model?.name,
                            label: item?.model?.name,
                          }));
                          setFieldValue('modelGroup', modelGroup[0].value);
                          const modelYear = newVehiclesData?.vehicles.map((item) => ({
                            value: item?.model?.year,
                            label: item?.model?.year,
                          }));
                          setFieldValue('model', modelYear[0].value?.toString());
                          const engineTypeApi = newVehiclesData?.vehicles.map((item) => {
                            const label =
                              fuelTypesList.find((fuelType) => fuelType.value === item?.model?.engine?.fuelTypeLabel)
                                ?.label ?? '';

                            return {
                              value: (item?.model?.engine?.fuelTypeLabel as FuelType) ?? '',
                              label,
                            };
                          });
                          setFieldValue('engine', engineTypeApi[0].value);
                          const model = newVehiclesData?.vehicles.map((item) => ({
                            value: item?.model?.description,
                            label: item?.model?.description,
                          }));
                          setFieldValue('modelName', model[0].value);
                          if (newVehiclesData?.vehicles[0].model?.manufacturer) {
                            setFieldValue('brand', newVehiclesData?.vehicles[0].model?.manufacturer);
                          }

                          const carPrice = btoPrices.grossPriceWithOptions;

                          setOriginalCatalogPrice(carPrice);
                          setFieldValue('catalogPriceBto', carPrice);

                          if (isEngineElectric(engineTypeApi[0]?.value)) {
                            setFieldValue('co2emision', 0);
                          } else {
                            const co2Emission = newVehiclesData?.vehicles.map(
                              (item) => item?.model?.economics?.emissions?.combined?.value
                            );

                            const configEqualsPrevious = isEqual(configurationString, previousConfigurationString);
                            const co2Value = configEqualsPrevious
                              ? (previousValuesRef.current.co2emision ?? co2Emission?.[0])
                              : co2Emission?.[0];

                            setFieldValue('co2emision', co2Value);
                            setCo2EmissionsIsSetByAPI(Boolean(co2Emission?.[0]));
                          }

                          const batteryPrice = newVehiclesData?.vehicles.map((item) => ({
                            value: item?.model?.battery?.batteryPrice?.grossAmount || null,
                          }));

                          if (isEngineElectricOrHybrid(engineTypeApi[0]?.value)) {
                            setFieldValue('batteryPrice', batteryPrice[0].value);
                          } else {
                            setFieldValue('batteryPrice', 0);
                          }

                          const milesCatalogId = newVehiclesData?.vehicles?.[0].externalVehicleIdentifiers?.find(
                            (identifier) => identifier.type === 'MILES_CATALOG_VEHICLE_ID'
                          )?.id;

                          if (milesCatalogId) {
                            setFieldValue('vehicleMilesId', milesCatalogId);
                          }
                          if (!values.currMileage) {
                            setFieldValue('currMileage', undefined);
                          }

                          store.dispatch(loadingActions.setLoading(false));
                          setConfigIsPrefilledWithNewData(true);
                        }
                      }
                      if (!quoteId || continueButtonLabel === continueLabel) {
                        fetchBTOVehiclesData();
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [vehicleType, jatoId]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      if (!values.vehicleMilesId || vehicleType === VehicleType.STOCK) return;
                      // If vehicleType NEW, only allow this endpoint when configIsPrefilledWithNewData
                      if (vehicleType === VehicleType.NEW && !configIsPrefilledWithNewData) return;

                      store.dispatch(loadingActions.setLoading(true));
                      getCatalogVehicle(values.vehicleMilesId)
                        .then((catalogVehicleResponse) => {
                          const fiscalPower =
                            catalogVehicleResponse?.vehicleData?.catalogFiscalPower ?? values.fiscalPower;
                          setFieldValue('fiscalPower', fiscalPower);

                          const dinHp =
                            catalogVehicleResponse?.vehicleData?.model?.engine?.powerDetails?.powerValues?.find(
                              (power) => power.unit === 'HORSEPOWER'
                            )?.value ?? values.dinHp;

                          setFieldValue('dinHp', dinHp);
                        })
                        .finally(() => {
                          store.dispatch(loadingActions.setLoading(false));
                        });

                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [values.vehicleMilesId, configIsPrefilledWithNewData]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      const vehicle = allDataForAllVehicles?.find(
                        (item: any) =>
                          item?.model?.year === Number(values?.model) &&
                          item?.model?.engine?.fuelTypeLabel === values?.engine &&
                          item?.model?.description === values?.modelName
                      );
                      if (vehicle) {
                        if (!isEngineElectric(values.engine)) {
                          setFieldValue('co2emision', vehicle?.model?.economics?.emissions?.combined?.value || null);
                        }
                        if (isEngineElectricOrHybrid(values.engine)) {
                          setFieldValue('batteryPrice', vehicle?.model?.battery?.batteryPrice?.grossAmount || null);
                        }
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [values?.modelName]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      if (!values?.engine || vehicleType === VehicleType.NEW) return;
                      const isElectric = isEngineElectric(values.engine);
                      const isElectricOrHybrid = isEngineElectricOrHybrid(values.engine);
                      setFieldValue(
                        'co2emision',
                        isElectric ? 0 : (previousValuesRef.current.co2emision ?? formInitialValues.co2emision)
                      );
                      setFieldTouched('co2emision', false);

                      if (isElectricOrHybrid) {
                        const batteryPriceEntered = values.batteryPrice && values.batteryPrice > 0;
                        if (!batteryPriceEntered) {
                          setFieldValue('batteryPrice', '');
                          setFieldTouched('batteryPrice', false);
                        }
                      } else {
                        setFieldValue('batteryPrice', 0);
                      }

                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [values?.engine]);

                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                      if (values.fiscalPower === undefined) return;
                      setFiscalPower(values.fiscalPower);
                    }, [values.fiscalPower]);

                    return (
                      <Form onSubmit={handleSubmit} className="u-pt-none">
                        {vehicleType === VehicleType.NEW && (
                          <NewVehicleBaseForm
                            isFormReadonly={isFormReadOnly}
                            co2EmissionsIsSetByAPI={co2EmissionsIsSetByAPI}
                            setShowDemoCarModal={setShowDemoCarModal}
                          />
                        )}
                        {vehicleType === VehicleType.USED && (
                          <UsedVehicleBaseForm
                            allDataForAllVehicles={allDataForAllVehicles}
                            manufacturersList={manufacturersList}
                            setShowDemoCarModal={setShowDemoCarModal}
                            vehicleGroupModelsList={vehicleGroupModelsList}
                            isFormReadonly={isFormReadOnly}
                          />
                        )}
                        {vehicleType === VehicleType.STOCK && (
                          <StockVehicleBaseForm
                            allDataForAllVehicles={allDataForAllVehicles}
                            manufacturersList={manufacturersList}
                            setShowDemoCarModal={setShowDemoCarModal}
                            vehicleGroupModelsList={vehicleGroupModelsList}
                            isFormReadonly={isFormReadOnly}
                            onVehicleModelSelect={(model) => {
                              setOriginalCatalogPrice(model?.carPrice || 0);
                              setFieldValue('catalogPrice', model?.carPrice || 0);
                            }}
                          />
                        )}

                        <Fieldset>
                          <Fieldset.Row className="u-text-right">
                            <Button id="" testId="" type="submit" disabled={isFormReadOnly}>
                              {isLoading ? <Spinner section center small /> : continueButtonLabel}
                            </Button>
                          </Fieldset.Row>
                        </Fieldset>
                        <DemoCarErrorModal
                          visible={showDemoCarModal}
                          vehicleType={vehicleType}
                          onCancel={() => {
                            setFieldValue('currMileage', undefined);
                            setFieldValue('regDate', '');
                            setShowDemoCarModal(false);
                          }}
                          onConfirm={() => {
                            setFieldValue('currMileage', undefined);
                            setFieldValue('regDate', '');
                            setShowDemoCarModal(false);
                          }}
                        />
                        <NewCopiedQuoteModal
                          visible={showNewCopiedQuoteModal}
                          onCancel={() => {
                            setShowNewCopiedQuoteModal(false);
                            formProps.resetForm({ values: previousValuesRef.current });
                            if (vehicleType === VehicleType.NEW) {
                              dispatch(setBtoConfigLocked(false));
                              dispatch(
                                setConfigurationString({
                                  config: JSON.parse(previousConfigurationString || ''),
                                  preservePrevious: true,
                                })
                              );
                              setConfigIsPrefilledWithNewData(false);
                            }
                            setBaseConfigurationSubmittedCorrectly(true);
                            setExpandedSection(ConfigurationsSection.ADDITIONALCONFIGURATION);
                          }}
                          onConfirm={() => {
                            setSkipQuoteModalCheck(true);
                            setShowNewCopiedQuoteModal(false);
                            dispatch(setIsNewCopiedQuoteSubmitted(true));
                            setScheduleSubmit(true);
                          }}
                        />
                        <NoVehiclesErrorModal
                          visible={showWarningPopup}
                          onCancel={() => {
                            setShowWarningPopup(false);
                          }}
                          jatoId={jatoId}
                        />
                      </Form>
                    );
                  }}
                </Formik>
              </FormSection>
            </ContentSection.ComponentWrapper>

            {/* Additional configuration */}
            <ContentSection.ComponentWrapper>
              <FormSection
                key="additionalConfiguration"
                id="additionalConfiguration"
                title={t('step1:configVehicle:additionalConfiguration:title')}
                status={additionalConfigurationSubmitted ? 'success' : undefined}
                disabled={!baseConfigurationSubmitted}
                defaultOpen={expandedSection === ConfigurationsSection.ADDITIONALCONFIGURATION}
                onChange={(open: any) => {
                  setExpandedSection(open ? ConfigurationsSection.ADDITIONALCONFIGURATION : null);
                }}
              >
                <AdditionalConfigurations
                  demoCarCheckbox={demoCarCheckbox}
                  expandedSection={expandedSection}
                  fiscalPower={fiscalPower}
                  rimOptions={rimOptions}
                  formState={additionalConfigurationsFromState}
                  initialValue={formInitialValues}
                  isLoading={isLoading}
                  setAdditionalConfigurationSubmitted={setAdditionalConfigurationSubmitted}
                  setExpandedSection={setExpandedSection}
                  setFiscalPower={setFiscalPower}
                  setIsLoading={setIsLoading}
                  stepValues={stepValues}
                  vehicleType={vehicleType}
                  quoteId={quoteId}
                  isFormReadonly={!isQuoteEditable}
                  prefillData={quoteData}
                />
              </FormSection>

              <Fieldset.Row>
                <Layout>
                  <Layout.Item default="1/3" s="1/1">
                    <TotalPriceField
                      name="totalPrice"
                      label={t('step1:configVehicle:totalPrice')}
                      notRequired
                      value={calculateQuoteFinancedAmount(quote).toFixed(2).toString().replace('.', ',') || '0'}
                    />
                  </Layout.Item>
                </Layout>
              </Fieldset.Row>
            </ContentSection.ComponentWrapper>
          </FormSectionGroup>
        </ContentSection>
        <Layout center>
          <Layout.Item default="1/2" s="1/1" className="u-mt">
            <ButtonContainer center>
              <Button
                type="button"
                secondary
                onClick={() => {
                  if (vehicleType === VehicleType.NEW) {
                    navigate.configurator();
                  }
                  resetQuoteSpecificReduxData();
                  window.history.replaceState({}, document.title);
                  navigate.selectCarType();
                }}
                disabled={!isQuoteEditable}
              >
                {t('step1:configVehicle:backButton')}
              </Button>
              <Button
                disabled={!(baseConfigurationSubmitted && additionalConfigurationSubmitted && expandedSection === null)}
                onClick={() => {
                  if (isQuoteEditable) {
                    navigate.selectCustomerType();
                  } else {
                    navigate.form(2);
                  }
                }}
                type="submit"
              >
                {isLoading && baseConfigurationSubmitted && additionalConfigurationSubmitted ? (
                  <Spinner section center small />
                ) : (
                  t('step1:configVehicle:valider')
                )}
              </Button>
            </ButtonContainer>
          </Layout.Item>
        </Layout>
      </PageWrap>
    </>
  );
};
